import type { ChildDom } from "vanjs-core";
import { modal } from "..";
import type { PriceWrapper } from "../billing/prices";
import type { DeviceIdType, DeviceType } from "../device/device";
import { t } from "../locale";
import type { RouteContext } from "../router";
import { centeredBox } from "./centered-box";
import { nameField } from "./device-name-field";
import { ownerField } from "./device-owner-field";
import { sharingField } from "./device-sharing-field";
import { subscriptionField } from "./device-subscription-field";
import { tagsField } from "./device-tags-field";
import { button, div, h1, h2, hr, p } from "./van-tags";

interface ManageDeviceBoxParams {
	ctx: RouteContext;
	deviceIdType: DeviceIdType;
	deviceType: DeviceType;
	fields: ChildDom[];
	getAccess: () => string[];
	getDeviceId: () => string;
	getHelpText: () => string;
	getName: () => string;
	getNotify: () => string[];
	getOwner: () => string;
	getPriceId: () => string;
	getPrices: () => Record<string, PriceWrapper>;
	getSignedInUser: () => User;
	getTags: () => string[];
	isOwner: () => boolean;
	save: () => Promise<void>;
	setAccess: (emails: string[]) => void;
	setName: (name: string) => void;
	setNotify: (emails: string[]) => void;
	setOwner: (email: string) => void;
	setPriceId: (priceId: string) => void;
	setTags: (tags: string[]) => void;
	unregister: () => Promise<void>;
}

export function deviceSettingsBox(args: ManageDeviceBoxParams): ChildDom {
	return centeredBox(
		{ size: "narrow" },
		h1({ class: "title is-4" }, args.deviceType),
		h2(
			{ class: "subtitle is-6" },
			() => `${args.deviceIdType} ${args.getDeviceId()}`,
		),
		nameField({
			getName: args.getName,
			setName: args.setName,
		}),
		ownerField({
			getAccess: args.getAccess,
			getOwner: args.getOwner,
			isDisabled: () => !args.isOwner(),
			setOwner: args.setOwner,
		}),
		sharingField({
			getAccess: args.getAccess,
			getNotify: args.getNotify,
			getOwner: args.getOwner,
			setAccess: args.setAccess,
			setNotify: args.setNotify,
		}),
		tagsField({
			getTags: args.getTags,
			setTags: args.setTags,
		}),
		subscriptionField({
			getPriceId: args.getPriceId,
			isDisabled: () => !args.isOwner(),
			getPrices: args.getPrices,
			setPriceId: args.setPriceId,
		}),
		hr(),
		...args.fields,
		hr(),
		div(
			{ class: "buttons" },
			button(
				{ class: "button is-primary", onclick: () => args.save() },
				t("deviceSettingsPage.saveButton"),
			),
			button(
				{
					class: "button is-primary",
					onclick: () =>
						args.ctx.router.jump(
							`/devices?id=${args.getDeviceId()}`,
						),
				},
				t("deviceSettingsPage.backButton"),
			),
			button(
				{
					class: "button is-danger",
					onclick: () => {
						modal.open({
							header: t(
								"deviceSettingsPage.confirmUnregistrationModal.header",
							)(),
							body: [p(t("deviceUnregistrationDisclaimer"))],
							footer: [
								button(
									{
										class: "button is-danger",
										onclick: () => {
											args.unregister().finally(
												modal.close,
											);
										},
									},
									t(
										"deviceSettingsPage.confirmUnregistrationModal.confirmButton",
									),
								),
								button(
									{
										class: "button",
										onclick: () => modal.close(),
									},
									t(
										"deviceSettingsPage.confirmUnregistrationModal.cancelButton",
									),
								),
							],
						});
					},
				},
				t("deviceSettingsPage.unregisterButton"),
			),
		),
		div(
			{ class: "field" },
			p(
				{
					class: () =>
						args.getHelpText() === "Ok"
							? "help is-success"
							: "help is-danger",
				},
				() => args.getHelpText(),
			),
		),
	);
}
